'use client';

import { useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import type { BulkPayload } from '@/types/bulk-action';
import { getSortingStateParser } from '@/lib/parsers';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  Loader2,
  Truck,
  Package,
  Users,
  MapPin,
  RefreshCw,
  Download,
  ArrowLeftRight
} from 'lucide-react';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useSelectAllPages } from '@/hooks/use-select-all-pages';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import { useHashParamDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import { Link } from '@/i18n/routing';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { SelectAllBanner } from '@/components/select-all-banner';
import { PermissionGate } from '@/features/auth';
import { RouteGate } from '@/components/route-gate';
import { SHIPPING_HASH } from '../constants';
import {
  driversApi,
  driverKeys,
  type Driver,
  type DriversListParams
} from '@/features/drivers';
import { shippingQueries, shipmentKeys } from '../services/shipping.queries';
import {
  useDeleteShipmentMutation,
  useChangeShipmentStatusMutation
} from '../services/shipping.mutations';
// import { ShippingMetrics } from './shipping-metrics';
import { ShippingFormDialog } from './shipping-form-dialog';
import { ShippingPriorityTag } from './shipping-priority-tag';
import { ShippingTableActionBar } from './shipping-table-action-bar';
import { ShippingCard } from './shipping-card';
import { ShippingCompactRow } from './shipping-compact-row';
import type {
  Shipment,
  ShipmentPriority,
  ShipmentStatus,
  ShipmentsListParams
} from '../types';

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
  'bg-indigo-500',
  'bg-teal-500'
];

function getAvatarColor(id: number): string {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

const statusStyles: Record<ShipmentStatus, string> = {
  draft:
    'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-700 dark:bg-orange-900/30 dark:text-orange-200',
  ready_for_shipping:
    'border-green-300 bg-green-50 text-green-800 dark:border-green-700 dark:bg-green-900/30 dark:text-green-200',
  in_transit:
    'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-200',
  received:
    'border-teal-300 bg-teal-50 text-teal-800 dark:border-teal-700 dark:bg-teal-900/30 dark:text-teal-200'
};

const statusDotColors: Record<ShipmentStatus, string> = {
  draft: 'bg-orange-500',
  ready_for_shipping: 'bg-green-500',
  in_transit: 'bg-blue-500',
  received: 'bg-teal-500'
};

const statusLabels: Record<ShipmentStatus, string> = {
  draft: 'statusDraft',
  ready_for_shipping: 'statusReadyForShipping',
  in_transit: 'statusInTransit',
  received: 'statusReceived'
};

const TRANSFERABLE_STATUSES: ShipmentStatus[] = [
  'draft',
  'ready_for_shipping',
  'in_transit'
];

function getAllowedTransitions(current: ShipmentStatus): ShipmentStatus[] {
  // Once a shipment is in transit, it cannot be reverted to draft —
  // boxes have been physically dispatched (aligned with OMA-524 box rules).
  if (current === 'in_transit') {
    return TRANSFERABLE_STATUSES.filter((s) => s !== current && s !== 'draft');
  }
  return TRANSFERABLE_STATUSES.filter((s) => s !== current);
}

export function ShippingListing() {
  const t = useTranslations('shipping');
  const queryClient = useQueryClient();
  // Overlay open-state lives in the URL hash (#edit/<id>, #delete/<id>) so
  // overlays are deep-linkable and close on browser Back. Destructure the
  // stable callbacks so they can go in hook dep arrays without tripping the
  // React Compiler's manual-memoization check.
  const {
    open: editOpen,
    param: editId,
    openWith: openEdit,
    onOpenChange: closeEdit
  } = useHashParamDialog(SHIPPING_HASH.edit);
  const {
    open: deleteOpen,
    param: deleteId,
    openWith: openDelete,
    onOpenChange: closeDelete
  } = useHashParamDialog(SHIPPING_HASH.delete);
  const [view, setView] = useDataView('shipping');

  const { data: metricsData } = useQuery(shippingQueries.metrics());
  const changeStatusMutation = useChangeShipmentStatusMutation();

  const mapDriverOption = useCallback(
    (d: Driver) => ({ value: String(d.id), label: d.name }),
    []
  );

  const driverInfinite = useInfiniteSelect<Driver, DriversListParams>({
    queryKey: driverKeys.lists(),
    queryFn: (params) => driversApi.getAll(params),
    mapOption: mapDriverOption
  });

  const columns: ColumnDef<Shipment>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
        />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40
    },
    {
      id: 'shipment_number',
      accessorKey: 'shipment_number',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('shipmentNumber')} />
      ),
      cell: ({ row }) => {
        const item = row.original;
        const isUrgent = item.priority === 'urgent';
        const driverName = item.driver?.name ?? '';
        const initials = driverName
          ? getInitials(driverName)
          : `#${item.shipment_number.slice(-2)}`;
        return (
          <div className='flex items-center gap-3'>
            <Avatar className='h-10 w-10 shrink-0'>
              <AvatarFallback
                className={cn(
                  'text-xs font-bold text-white',
                  getAvatarColor(item.id)
                )}
              >
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className='flex flex-col gap-0.5'>
              <div className='flex items-center gap-2'>
                <span
                  className={cn(
                    'font-mono text-sm font-medium',
                    isUrgent && 'text-red-600 dark:text-red-400'
                  )}
                >
                  #{item.shipment_number}
                </span>
                <ShippingPriorityTag priority={item.priority} />
              </div>
              <span
                className={cn(
                  'text-muted-foreground text-xs',
                  isUrgent && 'text-red-600 dark:text-red-400'
                )}
              >
                {item.shipment_date ?? item.created_at}
              </span>
              {item.location && (
                <span className='text-muted-foreground flex items-center gap-1 text-xs'>
                  <MapPin className='h-3 w-3' />
                  {item.location}
                </span>
              )}
            </div>
          </div>
        );
      },
      meta: {
        label: t('shipmentNumber'),
        permission: 'shipments.column.shipmentNumber'
      }
    },
    {
      id: 'driver',
      accessorFn: (row) => row.driver?.name ?? '',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('driverColumn')} />
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className='flex items-center gap-2'>
            <Users className='text-muted-foreground h-4 w-4 shrink-0' />
            <span className='text-sm'>{item.driver?.name ?? '—'}</span>
          </div>
        );
      },
      enableColumnFilter: true,
      meta: {
        label: t('driverColumn'),
        variant: 'infiniteMultiSelect' as const,
        permission: 'shipments.column.driverColumn'
      }
    },
    {
      id: 'box_count',
      accessorKey: 'box_count',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('boxCount')} />
      ),
      cell: ({ row }) => (
        <Badge variant='secondary' className='gap-1'>
          <Package className='h-3.5 w-3.5' />
          {t('boxCountLabel', { count: row.original.box_count })}
        </Badge>
      ),
      meta: { label: t('boxCount'), permission: 'shipments.column.boxCount' }
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('status')} />
      ),
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <div
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium',
              statusStyles[status]
            )}
          >
            <span
              className={cn(
                'inline-block h-2 w-2 shrink-0 rounded-full',
                statusDotColors[status]
              )}
            />
            {t(statusLabels[status])}
          </div>
        );
      },
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: t('status'),
        variant: 'multiSelect' as const,
        icon: 'solar:tag-bold-duotone',
        options: [
          { value: 'draft', label: t('statusDraft') },
          {
            value: 'ready_for_shipping',
            label: t('statusReadyForShipping'),
            count: metricsData?.data?.ready_for_shipping
          },
          { value: 'in_transit', label: t('statusInTransit') },
          { value: 'received', label: t('statusReceived') }
        ],
        permission: 'shipments.column.status'
      }
    },
    {
      id: 'priority',
      accessorKey: 'priority',
      header: () => null,
      cell: () => null,
      size: 0,
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: true,
      meta: {
        label: t('priority'),
        variant: 'multiSelect' as const,
        icon: 'solar:flag-bold-duotone',
        options: [
          { value: 'normal', label: t('priorityNormal') },
          { value: 'high', label: t('priorityHigh') },
          { value: 'urgent', label: t('priorityUrgent') }
        ],
        permission: 'shipments.column.priority'
      }
    },
    {
      id: 'actions',
      header: () => <div className='text-end'>{t('actions')}</div>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className='flex items-center justify-end gap-2'>
            {item.status !== 'received' && (
              <PermissionGate permissions={['shipments.edit']}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant='ghost'
                      size='sm'
                      className='h-8 gap-1 rounded-full border px-2 text-xs font-medium'
                      disabled={changeStatusMutation.isPending}
                    >
                      <ArrowLeftRight className='h-3 w-3' />
                      {t('transferAction')}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    {getAllowedTransitions(item.status).map((targetStatus) => (
                      <DropdownMenuItem
                        key={targetStatus}
                        onClick={() =>
                          changeStatusMutation.mutate({
                            id: item.id,
                            status: targetStatus
                          })
                        }
                      >
                        {t(statusLabels[targetStatus])}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </PermissionGate>
            )}
            <RouteGate route={`/dashboard/shipping/${item.id}`}>
              <Button
                variant='ghost'
                size='icon'
                className='h-8 w-8 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400'
                asChild
              >
                <Link href={`/dashboard/shipping/${item.id}`}>
                  <Eye className='h-4 w-4' />
                </Link>
              </Button>
            </RouteGate>
            {item.status !== 'received' && (
              <PermissionGate permissions={['shipments.edit']}>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-8 w-8 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400'
                  onClick={() => handleEdit(item)}
                >
                  <Pencil className='h-4 w-4' />
                </Button>
              </PermissionGate>
            )}
            {item.status !== 'received' && (
              <PermissionGate permissions={['shipments.delete']}>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-8 w-8 rounded-full bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
                  onClick={() => openDelete(String(item.id))}
                >
                  <Trash2 className='h-4 w-4' />
                </Button>
              </PermissionGate>
            )}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false
    }
  ];

  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlDriver] = useQueryState(
    'driver',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlStatus] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlPriority] = useQueryState(
    'priority',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<Shipment>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<Shipment>[])
  );

  const queryParams = useMemo<ShipmentsListParams>(() => {
    const params: ShipmentsListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlDriver.length > 0) {
      const ids = urlDriver.map(Number).filter((n) => n > 0);
      if (ids.length > 0) params.driver_id = ids;
    }
    params.sort_by = urlSorting[0]?.id ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';
    if (urlStatus.length) {
      params.status = urlStatus as ShipmentStatus[];
    }
    if (urlPriority.length) {
      params.priority = urlPriority as ShipmentPriority[];
    }
    return params;
  }, [urlPage, urlPerPage, urlDriver, urlSorting, urlStatus, urlPriority]);

  const { data, isPending, isPlaceholderData } = useQuery(
    shippingQueries.list(queryParams)
  );
  const deleteMutation = useDeleteShipmentMutation();

  const shipments = (data?.data ?? []) as Shipment[];
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  // Resolve the hash id param back to the loaded row (row must be on the
  // current page; deep-linking an off-page id simply won't open the overlay).
  const editTarget = editOpen
    ? (shipments.find((s) => String(s.id) === editId) ?? null)
    : null;
  const deleteTarget = deleteOpen
    ? (shipments.find((s) => String(s.id) === deleteId) ?? null)
    : null;

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<Shipment>({
    columns: filteredColumns,
    pageKey: 'shipping',
    data: shipments,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const selectAll = useSelectAllPages(table, pagination?.total ?? 0);

  const getBulkPayload = useCallback((): BulkPayload => {
    if (selectAll.isAllPagesSelected) {
      const { page, per_page, sort_by, sort_direction, ...filters } =
        queryParams;
      return { mode: 'all', filters };
    }
    const selectedIds = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    return { mode: 'ids', ids: selectedIds };
  }, [selectAll.isAllPagesSelected, queryParams, table]);

  const handleBulkComplete = useCallback(() => {
    selectAll.reset();
  }, [selectAll]);

  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('shipmentNumber'), value: 'shipment_number' },
      { label: t('boxCount'), value: 'box_count' },
      { label: t('status'), value: 'status' },
      { label: t('priority'), value: 'priority' },
      { label: t('shipmentDate'), value: 'shipment_date' },
      { label: t('createdAt'), value: 'created_at' }
    ],
    [t]
  );

  const handleEdit = useCallback(
    (item: Shipment) => openEdit(String(item.id)),
    [openEdit]
  );

  const handleDeleteTarget = useCallback(
    (item: Shipment) => openDelete(String(item.id)),
    [openDelete]
  );

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
    } catch {
      // handled by global MutationCache
    }
    closeDelete(false);
  }

  function handleRefresh() {
    queryClient.invalidateQueries({ queryKey: shipmentKeys.all });
  }

  function handleExport() {
    if (shipments.length === 0) return;
    const headers = [
      t('shipmentNumber'),
      t('driverColumn'),
      t('boxCount'),
      t('status'),
      t('priority'),
      t('location'),
      t('shipmentDate')
    ];
    const rows = shipments.map((s) => [
      s.shipment_number,
      s.driver?.name ?? '',
      String(s.box_count),
      s.status,
      s.priority,
      s.location ?? '',
      s.shipment_date ?? ''
    ]);
    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      )
      .join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `shipments-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h1 className='text-2xl font-bold tracking-tight'>
            {t('pageTitle')}
          </h1>
          <p className='text-muted-foreground text-sm'>{t('pageSubtitle')}</p>
        </div>
        <div className='flex items-center gap-2'>
          <Button variant='outline' size='sm' onClick={handleRefresh}>
            <RefreshCw className='me-2 h-4 w-4' />
            {t('refresh')}
          </Button>
          <Button variant='outline' size='sm' onClick={handleExport}>
            <Download className='me-2 h-4 w-4' />
            {t('export')}
          </Button>
          <PermissionGate permissions={['shipments.create']}>
            <Button asChild>
              <Link href='/dashboard/shipping/new'>
                <Plus className='me-2 h-4 w-4' />
                {t('addNew')}
              </Link>
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* <ShippingMetrics /> */}

      {/* View Toggle */}
      <div className='flex items-center justify-end gap-2'>
        {view !== 'table' && (
          <SortControl
            options={sortOptions}
            sorting={tableState.sorting}
            onSortingChange={(s) => table.setSorting(s)}
          />
        )}
        <DataViewToggle
          value={view}
          onValueChange={setView}
          pageKey='shipping'
        />
      </div>

      <DataTableToolbar
        table={table}
        viewFilterPermission='shipments.view_filter'
        infiniteConfigs={{ driver: driverInfinite }}
      />

      {isPending ? (
        <ListingSkeleton view={view} />
      ) : shipments.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <Truck className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noShipments')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {shipments.map((shipment) => (
              <ShippingCard
                key={shipment.id}
                shipment={shipment}
                onEdit={handleEdit}
                onDelete={handleDeleteTarget}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {shipments.map((shipment) => (
              <ShippingCompactRow
                key={shipment.id}
                shipment={shipment}
                onEdit={handleEdit}
                onDelete={handleDeleteTarget}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col gap-2',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <SelectAllBanner
            isAllPagesSelected={selectAll.isAllPagesSelected}
            isCurrentPageFullySelected={selectAll.isCurrentPageFullySelected}
            currentPageCount={shipments.length}
            totalCount={pagination?.total ?? 0}
            pageSize={tableState.pagination.pageSize}
            onSelectAllPages={() => selectAll.setAllPagesSelected(true)}
            onClearSelection={() => selectAll.reset()}
          />
          <DataTable
            table={table}
            actionBar={
              <ShippingTableActionBar
                table={table}
                getBulkPayload={getBulkPayload}
                onComplete={handleBulkComplete}
                isAllPagesSelected={selectAll.isAllPagesSelected}
                totalCount={pagination?.total ?? 0}
              />
            }
          />
        </div>
      )}

      <ShippingFormDialog
        open={editOpen && !!editTarget}
        onOpenChange={closeEdit}
        shipment={editTarget}
      />

      <AlertDialog
        open={deleteOpen && !!deleteTarget}
        onOpenChange={closeDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirmDescription', {
                number: deleteTarget?.shipment_number ?? ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {deleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
